import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Header from 'components/templates/layout/Header';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  menuList,
  KAKAO_OPEN_CHAT,
  CONSULTATION_EXTERNAL,
} from 'utils/constants';
import useUser from 'app/user/useUser';
import PricingModal, {
  shouldShowPricingModal,
} from 'components/blocks/modal/PricingModal';

const PRICING_MODAL_HIDDEN_PATHS = ['/pricing', '/subscribe', '/subscription'];

const PrivateLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLogin, isFreeUser } = useUser();
  const [showPricingModal, setShowPricingModal] = useState(false);

  useEffect(() => {
    if (!isLogin || !isFreeUser) return;
    const onHiddenPath = PRICING_MODAL_HIDDEN_PATHS.some((p) =>
      location.pathname.startsWith(p),
    );
    if (onHiddenPath) return;
    if (shouldShowPricingModal()) setShowPricingModal(true);
  }, [isLogin, isFreeUser, location.pathname]);

  return (
    <Container>
      <TopBar>
        <NavLinks>
          {menuList.map((group) =>
            group.child.map((item) => (
              <NavLink
                key={item.link}
                isActive={location.pathname === item.link}
                onClick={() => navigate(item.link)}
              >
                {item.name}
              </NavLink>
            )),
          )}
          <ExternalNavLink
            href={KAKAO_OPEN_CHAT.link}
            target="_blank"
            rel="noreferrer"
          >
            {KAKAO_OPEN_CHAT.name}
          </ExternalNavLink>
        </NavLinks>
        <RightSide>
          <ConsultationCTA
            href={CONSULTATION_EXTERNAL.link}
            target="_blank"
            rel="noreferrer"
          >
            {CONSULTATION_EXTERNAL.name}
          </ConsultationCTA>
          <Header />
        </RightSide>
      </TopBar>
      <Main>
        <Content>
          <Outlet />
        </Content>
      </Main>
      {showPricingModal && (
        <PricingModal onClose={() => setShowPricingModal(false)} />
      )}
    </Container>
  );
};

const Container = styled.div`
  background-color: #f4f7fe;
  position: relative;
  min-height: 100vh;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #fff;
  padding: 0 40px;
  height: 64px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  position: sticky;
  top: 0;
  z-index: 100;
`;

const NavLinks = styled.div`
  display: flex;
  gap: 8px;
`;

const navItemStyles = `
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  line-height: 1;
  letter-spacing: -0.02rem;
  border-radius: 8px;
  text-decoration: none;
  cursor: pointer;
  box-sizing: border-box;
  transition: all 0.2s;
`;

const NavLink = styled.div`
  ${navItemStyles}
  font-weight: ${({ isActive }) => (isActive ? '700' : '500')};
  color: ${({ isActive }) => (isActive ? '#644ded' : '#4a5568')};
  background-color: ${({ isActive }) => (isActive ? '#f0edff' : 'transparent')};

  &:hover {
    background-color: ${({ isActive }) => (isActive ? '#f0edff' : '#f7f7f7')};
    color: #644ded;
  }
`;

const ExternalNavLink = styled.a`
  ${navItemStyles}
  color: #4a5568;

  &::before {
    content: '';
    display: inline-block;
    width: 8px;
    height: 8px;
    background-color: #fbe300;
    border-radius: 50%;
  }

  &:hover {
    background-color: #fffbe0;
    color: #644ded;
  }
`;

const RightSide = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ConsultationCTA = styled.a`
  ${navItemStyles}
  height: 40px;
  padding: 0 18px;
  font-weight: 700;
  font-size: 14px;
  color: #fff;
  background-color: #644ded;
  box-shadow: 0 4px 12px rgba(100, 77, 237, 0.25);

  &:hover {
    background-color: #5238d4;
    color: #fff;
    box-shadow: 0 6px 16px rgba(100, 77, 237, 0.35);
    transform: translateY(-1px);
  }
`;

const Main = styled.div`
  display: flex;
`;

const Content = styled.div`
  position: relative;
  width: 100%;
  padding: 20px 35px;
`;
export default React.memo(PrivateLayout);
